import { Controller } from "@hotwired/stimulus";

/**
 * Expandable Controller
 *
 * Handles expandable content areas with a gradient fade and "View more" button.
 * Only shows controls when content height exceeds maxHeight.
 *
 * @example Basic Usage
 * ```html
 * <div data-controller="expandable">
 *   <div data-expandable-target="content" class="overflow-hidden relative">
 *     <!-- Your content here -->
 *   </div>
 *   <div data-expandable-target="gradient" class="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-neutral-50 to-transparent pointer-events-none"></div>
 *   <a href="" data-expandable-target="button" data-action="expandable#toggle:prevent" data-turbo="false" class="link text-sm mt-2 block">
 *     View more
 *   </a>
 * </div>
 * ```
 *
 * @example Custom Max Height
 * ```html
 * <div data-controller="expandable" data-expandable-max-height-value="400">
 *   <!-- Same structure as above -->
 * </div>
 * ```
 */
export default class extends Controller {
  /** Main content area that will be expanded/collapsed. Limited to maxHeight when collapsed. */
  /** @target content */

  /** "View more" button. Only visible when content needs expansion and is collapsed. */
  /** @target button */

  /** Fade gradient at bottom of collapsed content. Only visible when content needs expansion. */
  /** @target gradient */
  static targets = ["content", "button", "gradient"];

  /** Maximum height in pixels before content is collapsed. Default: 256px */
  static values = {
    maxHeight: { type: Number, default: 256 },
  };

  /** Initializes expandable state based on content height. */
  connect() {
    this.updateButtonVisibility();
  }

  /** Toggles expanded state of content. */
  /** @action */
  toggle() {
    this.contentTarget.dataset.expanded =
      this.contentTarget.dataset.expanded !== "true";
    this.updateButtonVisibility();
  }

  /** Updates visibility of button and gradient based on content height and expanded state. */
  updateButtonVisibility() {
    const contentHeight = this.contentTarget.scrollHeight;
    const needsExpansion = contentHeight > this.maxHeightValue;

    if (this.hasButtonTarget) {
      this.buttonTarget.classList.toggle(
        "hidden",
        !needsExpansion || this.contentTarget.dataset.expanded === "true",
      );
    }
    if (this.hasGradientTarget) {
      this.gradientTarget.classList.toggle(
        "hidden",
        !needsExpansion || this.contentTarget.dataset.expanded === "true",
      );
    }

    if (!this.contentTarget.dataset.expanded) {
      this.contentTarget.style.maxHeight = needsExpansion
        ? `${this.maxHeightValue}px`
        : "none";
    } else {
      this.contentTarget.style.maxHeight = "none";
    }
  }
}
